import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useSearch } from "wouter";
import { Layout } from "@/components/layout/Layout";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Separator } from "@/components/ui/separator";
import { Users2, Search, Plus, Loader2, RotateCcw, Copy } from "lucide-react";
import { api, Account, Contact, ContactInput } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

function CopyBtn({ value }: { value: string }) {
  const { toast } = useToast();
  return (
    <button
      className="text-slate-500 hover:text-foreground transition-colors shrink-0 ml-1 p-0.5 rounded hover:bg-muted align-middle"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigator.clipboard.writeText(value); toast({ title: "Copied!" }); }}
      title="Copy to clipboard"
    >
      <Copy className="w-4 h-4" />
    </button>
  );
}

const emptyFormData: ContactInput = {
  account_id: "",
  name: "",
  email: "",
  phone: "",
  phone_label: "",
  mobile: "",
  mobile_label: "",
  address: "",
  notes: "",
  should_remind: true,
  auto_sync_emails: true,
  show_reminder_dialog: true,
  is_primary: false,
};

export default function Contacts() {
  const searchParams = useSearch();
  const urlParams = new URLSearchParams(searchParams);
  const accountFromUrl = urlParams.get("account");
  
  const [searchQuery, setSearchQuery] = useState("");
  const [accountFilter, setAccountFilter] = useState<string>(accountFromUrl || "all");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContact, setEditingContact] = useState<Contact | null>(null);
  const [formData, setFormData] = useState<ContactInput>(emptyFormData);
  const { toast } = useToast();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (accountFromUrl) {
      setAccountFilter(accountFromUrl);
    }
  }, [accountFromUrl]);

  const { data: contacts = [], isLoading } = useQuery<Contact[]>({
    queryKey: ["contacts", accountFilter, statusFilter, searchQuery],
    queryFn: () => api.getContacts({
      accountId: accountFilter !== "all" ? accountFilter : undefined,
      status: statusFilter,
      search: searchQuery || undefined,
    }),
  });

  const { data: accounts = [] } = useQuery<Account[]>({
    queryKey: ["accounts"],
    queryFn: () => api.getAccounts(false),
  });

  const createMutation = useMutation({
    mutationFn: (data: ContactInput) => api.createContact(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast({ title: "Contact created successfully" });
      closeDialog();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ContactInput> }) => 
      api.updateContact(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast({ title: "Contact updated successfully" });
      closeDialog();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (id: string) => api.deleteContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast({ title: "Contact deactivated" });
      closeDialog();
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const reactivateMutation = useMutation({
    mutationFn: (id: string) => api.reactivateContact(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
      toast({ title: "Contact reactivated" });
    },
    onError: (error: any) => {
      toast({ title: "Error", description: error.message, variant: "destructive" });
    },
  });

  const closeDialog = () => {
    setDialogOpen(false);
    setEditingContact(null);
    setFormData(emptyFormData);
  };

  const openCreateDialog = () => {
    setEditingContact(null);
    setFormData(emptyFormData);
    setDialogOpen(true);
  };

  const openEditDialog = (contact: Contact) => {
    setEditingContact(contact);
    setFormData({
      account_id: contact.account_id,
      name: contact.name,
      email: contact.email || "",
      phone: contact.phone || "",
      phone_label: contact.phone_label || "",
      mobile: contact.mobile || "",
      mobile_label: contact.mobile_label || "",
      address: contact.address || "",
      notes: contact.notes || "",
      should_remind: contact.should_remind,
      auto_sync_emails: contact.auto_sync_emails,
      show_reminder_dialog: contact.show_reminder_dialog,
      is_primary: contact.is_primary,
    });
    setDialogOpen(true);
  };

  const handleSubmit = () => {
    if (!formData.account_id || !formData.name.trim()) {
      toast({ title: "Error", description: "Account and name are required", variant: "destructive" });
      return;
    }

    if (editingContact) {
      updateMutation.mutate({ id: editingContact.id, data: formData });
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleDeactivate = () => {
    if (editingContact) {
      deactivateMutation.mutate(editingContact.id);
    }
  };

  const handleReactivate = (contact: Contact) => {
    reactivateMutation.mutate(contact.id);
  };

  const isSaving = createMutation.isPending || updateMutation.isPending || deactivateMutation.isPending;
  const isInactive = editingContact?.deleted_at != null;

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-heading font-bold" data-testid="text-page-title">Contacts</h1>
            <p className="text-muted-foreground mt-1">Manage client contacts</p>
          </div>
          <Button onClick={openCreateDialog} data-testid="button-new-contact">
            <Plus className="w-4 h-4 mr-2" />
            New Contact
          </Button>
        </div>

        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                  data-testid="input-search"
                />
              </div>
              <div className="flex items-center gap-3">
                <Select value={accountFilter} onValueChange={setAccountFilter}>
                  <SelectTrigger className="w-[200px]" data-testid="select-account-filter">
                    <SelectValue placeholder="All Accounts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Accounts</SelectItem>
                    {accounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        {account.account_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-[140px]" data-testid="select-status-filter">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="inactive">Inactive</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : contacts.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Users2 className="w-12 h-12 mx-auto mb-4 opacity-50" />
                <p>No contacts found</p>
                {searchQuery && <p className="text-sm mt-1">Try adjusting your search or filters</p>}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Account</TableHead>
                    <TableHead className="text-center">Primary</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="w-24"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {contacts.map((contact) => (
                    <TableRow 
                      key={contact.id} 
                      className="cursor-pointer hover:bg-muted/50"
                      onClick={() => openEditDialog(contact)}
                      data-testid={`row-contact-${contact.id}`}
                    >
                      <TableCell className="font-medium">{contact.name}</TableCell>
                      <TableCell className="text-muted-foreground">
                        {contact.email || "-"}
                        {contact.email && <CopyBtn value={contact.email} />}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {contact.phone || "-"}
                        {contact.phone && <CopyBtn value={contact.phone} />}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="font-normal">
                          {contact.accounts?.account_name || "-"}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-center">
                        {contact.is_primary ? (
                          <Badge className="bg-primary">Yes</Badge>
                        ) : (
                          <span className="text-muted-foreground text-sm">No</span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {contact.deleted_at ? (
                          <Badge variant="secondary">Inactive</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                        )}
                      </TableCell>
                      <TableCell>
                        {contact.deleted_at && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleReactivate(contact);
                            }}
                            disabled={reactivateMutation.isPending}
                            data-testid={`button-reactivate-${contact.id}`}
                          >
                            <RotateCcw className="w-4 h-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      <Dialog open={dialogOpen} onOpenChange={(open) => !open && closeDialog()}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingContact ? "Edit Contact" : "New Contact"}</DialogTitle>
            <DialogDescription>
              {editingContact ? "Update contact information" : "Add a new contact to a client account"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="account">Account / Client *</Label>
              <Select 
                value={formData.account_id} 
                onValueChange={(v) => setFormData({ ...formData, account_id: v })}
              >
                <SelectTrigger data-testid="select-contact-account">
                  <SelectValue placeholder="Select an account" />
                </SelectTrigger>
                <SelectContent>
                  {accounts.map((account) => (
                    <SelectItem key={account.id} value={account.id}>
                      {account.account_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Contact name"
                data-testid="input-contact-name"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="email@example.com"
                data-testid="input-contact-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <div className="flex gap-2">
                <Input
                  id="phone"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="(555) 123-4567"
                  data-testid="input-contact-phone"
                  className="flex-1"
                />
                <Input
                  value={formData.phone_label}
                  onChange={(e) => setFormData({ ...formData, phone_label: e.target.value })}
                  placeholder="Label (e.g. Direct, Office)"
                  className="w-44"
                  data-testid="input-contact-phone-label"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="mobile">Mobile</Label>
              <div className="flex gap-2">
                <Input
                  id="mobile"
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  placeholder="(555) 987-6543"
                  data-testid="input-contact-mobile"
                  className="flex-1"
                />
                <Input
                  value={formData.mobile_label}
                  onChange={(e) => setFormData({ ...formData, mobile_label: e.target.value })}
                  placeholder="Label (e.g. Work Cell, Personal)"
                  className="w-44"
                  data-testid="input-contact-mobile-label"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 Main St, City, State 12345"
                rows={2}
                data-testid="input-contact-address"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes</Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Additional notes about this contact..."
                rows={2}
                data-testid="input-contact-notes"
              />
            </div>

            <div className="flex items-center space-x-2">
              <Switch
                id="isPrimary"
                checked={formData.is_primary}
                onCheckedChange={(checked) => setFormData({ ...formData, is_primary: checked })}
                data-testid="switch-is-primary"
              />
              <Label htmlFor="isPrimary">Primary contact for this account</Label>
            </div>

            <Separator />

            <div className="space-y-3">
              <Label className="text-sm font-medium">Reminder Settings</Label>
              
              <div className="flex items-center space-x-2">
                <Switch
                  id="shouldRemind"
                  checked={formData.should_remind}
                  onCheckedChange={(checked) => setFormData({ ...formData, should_remind: checked })}
                  data-testid="switch-should-remind"
                />
                <Label htmlFor="shouldRemind" className="font-normal">Include in reminder emails</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="autoSyncEmails"
                  checked={formData.auto_sync_emails}
                  onCheckedChange={(checked) => setFormData({ ...formData, auto_sync_emails: checked })}
                  data-testid="switch-auto-sync"
                />
                <Label htmlFor="autoSyncEmails" className="font-normal">Auto sync emails</Label>
              </div>

              <div className="flex items-center space-x-2">
                <Switch
                  id="showReminderDialog"
                  checked={formData.show_reminder_dialog}
                  onCheckedChange={(checked) => setFormData({ ...formData, show_reminder_dialog: checked })}
                  data-testid="switch-reminder-dialog"
                />
                <Label htmlFor="showReminderDialog" className="font-normal">Show reminder dialog</Label>
              </div>
            </div>
          </div>

          <DialogFooter className="flex-col sm:flex-row gap-2">
            {editingContact && !isInactive && (
              <Button 
                variant="destructive" 
                onClick={handleDeactivate}
                disabled={isSaving}
                className="w-full sm:w-auto sm:mr-auto"
                data-testid="button-deactivate-contact"
              >
                Deactivate
              </Button>
            )}
            <Button variant="outline" onClick={closeDialog} disabled={isSaving}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={isSaving || !formData.account_id || !formData.name.trim()}
              data-testid="button-save-contact"
            >
              {isSaving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {editingContact ? "Save Changes" : "Create Contact"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
